// @file Post sorting related functions for all formats
import { isNonEmojiReactionData } from '@@/bits/emoji'
import { ww } from '@@/bits/global'
import { getCustomPropertyValue } from '@@/bits/post_properties'
import { isPostPinned } from '@@/bits/post_state'
import { getPostPublishedAt } from '@@/bits/post_timestamps'
import type { SortOrder } from '@@/bits/surface_settings_helper'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { type AccumulatedReactions, type Post, type WallCustomPostProperty } from '@@/types'
import { ReactionType } from '@padlet/arvo'
import { orderBy, sortBy } from 'es-toolkit'
import type { Store } from 'vuex'

// Assumption: by the time surface/sorter (i.e. this file) is used,
// the store would have been initialized.
interface SurfaceXstore {
  store: Store<any>
}
const store = (): Store<any> => (ww.xstore as unknown as SurfaceXstore)?.store

function getAbsoluteSortIndex(ts: Date | number = new Date(), wallCreatedAt = store().getters.wallCreatedAt): number {
  const timestamp = ts instanceof Date ? ts.getTime() : ts
  const absSortIndex = 1024 * (timestamp - new Date(wallCreatedAt).getTime())
  const postMaxAbsSortIndexWithBuffer = (store().getters['post/postMaxAbsSortIndex'] as number) + 1024
  return Math.max(absSortIndex, postMaxAbsSortIndexWithBuffer)
}

function getSortIndex(): number {
  const absSortIndex = getAbsoluteSortIndex()
  // sections flip = false, timeline -> new posts added to bottom
  // sections flip = false, timeline_v2 -> new posts added to bottom
  // sections flip = true, timeline -> new posts added to bottom
  // sections flip = true, timeline_v2 -> new posts added to top/bottom (you can change this)
  if (store().getters.format === 'timeline') return absSortIndex
  if (store().getters.format === 'timeline_v2' && !useSurfaceStore().canUseSections) return -absSortIndex
  const postAtBottom =
    useSurfaceStore().newPostLocation === 'bottom' && useSurfaceSettingsStore().canConfigNewPostLocation
  return postAtBottom ? -absSortIndex : absSortIndex
}

function getTopSortIndex(): number {
  return getAbsoluteSortIndex()
}

function getBottomSortIndex(): number {
  return -getAbsoluteSortIndex()
}

/**
 * Returns the sort_index for a new unpinned post.
 * Unpinning a post should move it to between the last pinned post and the first unpinned post.
 * Optionally returns the gap_size in case we need it to reset sort_index for all posts.
 */
function getNewUnpinSortIndex(): { sortIndex: number; gapSize?: number } {
  const pinnedPosts = document.querySelectorAll('.wish[data-pinned="true"]')
  if (pinnedPosts.length === 0) {
    // there's no pinned posts left -> return the top sort index
    return { sortIndex: getTopSortIndex() }
  }

  const unpinnedPosts = document.querySelectorAll('.wish[data-pinned="false"]')
  if (unpinnedPosts.length === 0) {
    // there's no unpinned posts left -> return the bottom sort index
    return { sortIndex: getBottomSortIndex() }
  }

  const lastPinnedPost = pinnedPosts[pinnedPosts.length - 1]
  const firstUnpinnedPost = unpinnedPosts[0]
  // else, return the sort index between these posts
  const prevIndex = parseInt(lastPinnedPost.getAttribute('data-rank') as string, 10)
  const nextIndex = parseInt(firstUnpinnedPost.getAttribute('data-rank') as string, 10)
  const sortIndex = Math.round((prevIndex + nextIndex) / 2)
  const gapSize = Math.abs(prevIndex - nextIndex)
  return { sortIndex, gapSize }
}

interface ComputeNewPostIndexOptions {
  sectionId?: number
  prevId?: string | null
  nextId?: string | null
}

function computePostIndexFromPost(
  /*
    If post is of type string, it is the selector to the post element.
    If post is of type HTMLElement, it is the post itself.
    If post is null, we get the sort index between 2 posts. (Mostly used in timeline v1 format)
   */
  post: null | string | HTMLElement,
  options: ComputeNewPostIndexOptions = {},
): { sortIndex: number | null; sectionId?: number; gapSize?: number | null } {
  let currentPost: null | HTMLElement = null
  if (post == null) {
    currentPost = null
  } else if (typeof post === 'string') {
    currentPost = document.querySelector(post)
  } else {
    currentPost = post
  }
  const sectionId = options.sectionId

  let previousPost: null | Element
  if (
    options.prevId === undefined &&
    currentPost != null &&
    currentPost.previousElementSibling?.matches('.wish') === true
  ) {
    previousPost = currentPost.previousElementSibling
  } else if (options.prevId != null) {
    previousPost = document.querySelector(`#${options.prevId}`)
  } else {
    previousPost = null
  }

  let nextPost: null | Element
  if (
    options.nextId === undefined &&
    currentPost != null &&
    currentPost.nextElementSibling?.matches('.wish') === true
  ) {
    nextPost = currentPost.nextElementSibling
  } else if (options.nextId != null) {
    nextPost = document.querySelector(`#${options.nextId}`)
  } else {
    nextPost = null
  }

  if (currentPost != null && currentPost.getAttribute('data-pinned') === 'true') {
    // Dropping a pinned post before an unpinned post -> don't take next unpinned post into account
    if (nextPost != null && nextPost.getAttribute('data-pinned') === 'false') {
      nextPost = null
    }
  } else {
    // Dropping an unpinned post after a pinned post -> don't take previous pinned post into account
    if (previousPost != null && previousPost.getAttribute('data-pinned') === 'true') {
      previousPost = null
    }
  }

  let nextIndex: number | null = null
  let prevIndex: number | null = null
  let sortIndex: number | null = null
  if (nextPost == null && previousPost == null) {
    sortIndex = sectionId === null ? null : getSortIndex()
  } else {
    if (nextPost == null) {
      nextIndex = getBottomSortIndex()
    } else if (previousPost == null) {
      prevIndex = getTopSortIndex()
    }
    if (nextIndex == null && nextPost != null) {
      nextIndex = parseInt(nextPost.getAttribute('data-rank') as string, 10)
    }
    if (prevIndex == null && previousPost != null) {
      prevIndex = parseInt(previousPost.getAttribute('data-rank') as string, 10)
    }
    if (nextIndex != null && prevIndex != null) {
      sortIndex = Math.round((prevIndex + nextIndex) / 2)
    } else {
      sortIndex = null
    }
  }

  let gapSize: number | null = null

  if (sortIndex != null && prevIndex != null && nextIndex != null) {
    gapSize = Math.abs(prevIndex - nextIndex)
  }

  return {
    sortIndex,
    sectionId,
    gapSize,
  }
}

function computeAndSaveNewPostIndex(post: HTMLElement, options: ComputeNewPostIndexOptions = {}): void {
  const { sortIndex, sectionId, gapSize } = computePostIndexFromPost(post, options)

  if (sortIndex == null) return
  void store().dispatch('post/sortedPost', {
    postCid: post.getAttribute('data-post-cid'),
    sortIndex,
    sectionId,
    gapSize,
  })
}

/**
 * Sort posts by custom property values.
 *
 * Handles different property types:
 * - date: Sort chronologically
 * - number: Sort numerically
 * - text: Sort alphabetically
 * - single_select: Sort by option order
 * - vote: Sort by net votes (upvotes - downvotes), same as legacy like/vote reactions
 * - rating: Sort by Bayesian average, same as legacy star/grade reactions
 *
 * Note: Vote and rating properties maintain exact parity with legacy reaction sorting
 * to ensure seamless migration from reactions to custom properties.
 */
function sortPostsByCustomProp({
  posts,
  customProperty,
  sortOrder,
}: {
  posts: Post[]
  customProperty: WallCustomPostProperty
  sortOrder: SortOrder
}): Post[] {
  // By default we sort posts with undefined custom property values to the end
  const defaultSortValue = sortOrder === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY

  switch (customProperty?.data_type) {
    case 'date': {
      return orderBy(
        posts,
        [
          (post: Post): number | undefined => {
            const datePropValue = getCustomPropertyValue(post, customProperty)
            if (datePropValue == null) {
              return defaultSortValue
            }
            return new Date(datePropValue).getTime()
          },
          getPostPublishedAt,
        ],
        [sortOrder, 'asc'],
      )
    }
    case 'number': {
      return orderBy(
        posts,
        [
          (post: Post): number | undefined => {
            const numberPropValue = getCustomPropertyValue(post, customProperty)
            if (numberPropValue == null) {
              return defaultSortValue
            }
            return numberPropValue
          },
          getPostPublishedAt,
        ],
        [sortOrder, 'asc'],
      )
    }
    case 'text': {
      return sortPostsAlphabeticallyByAttribute(
        posts,
        (post: Post): string | undefined => {
          return getCustomPropertyValue(post, customProperty)
        },
        sortOrder,
      )
    }
    case 'single_select': {
      return orderBy(
        posts,
        [
          (post: Post): number | undefined => {
            const optionId = getCustomPropertyValue(post, customProperty)
            if (optionId == null) {
              return defaultSortValue
            }
            const option = customProperty?.selection_options?.find((option) => option.id === optionId)
            if (option == null) {
              return defaultSortValue
            }
            return option?.sort_index
          },
          getPostPublishedAt,
        ],
        [sortOrder, 'asc'],
      )
    }
    case 'vote': {
      // Vote properties: Sort by sum (net votes = upvotes - downvotes)
      // Similar to like/vote reactions sorting
      return orderBy(
        posts,
        [
          (post: Post): number => {
            const votePropValue = getCustomPropertyValue(post, customProperty)
            // value == null or value == { sum: 0, count: 0 } -> no votes.
            if (votePropValue == null) {
              return 0
            }
            // Edge case: Differentiate between posts with no votes vs votes that sum to 0
            // (e.g., 5 upvotes + 5 downvotes = sum of 0, but should rank differently than no votes)
            if (votePropValue.sum === 0) {
              if ((votePropValue.count ?? 0) > 0) {
                return 0.5 // Has votes but they cancel out
              }
              return 0
            }
            // Sort by sum (net votes: upvotes - downvotes), same as like/vote reactions
            return votePropValue.sum ?? 0
          },
          getPostPublishedAt,
        ],
        [sortOrder, 'asc'],
      )
    }
    case 'rating': {
      // Rating properties: Sort by Bayesian average, similar to star/grade reactions
      // 1. Compute global average across all posts
      const ratingValues = posts
        .map((post) => getCustomPropertyValue(post, customProperty))
        .filter((v) => v != null && (v.count ?? 0) > 0)

      const totalSum = ratingValues.reduce((acc, v) => acc + (v?.sum ?? 0), 0)
      const totalCount = ratingValues.reduce((acc, v) => acc + (v?.count ?? 0), 0)
      const globalAverage = totalCount > 0 ? totalSum / totalCount : 0

      // 2. Compute confidence number (median of rating counts)
      // This represents how many ratings a post needs to be "trusted"
      const sortedByCount = [...ratingValues].sort((a, b) => (a?.count ?? 0) - (b?.count ?? 0))
      const confidenceNumber = sortedByCount[Math.floor(sortedByCount.length / 2)]?.count ?? 100

      return orderBy(
        posts,
        [
          (post: Post): number => {
            const ratingPropValue = getCustomPropertyValue(post, customProperty)
            if (ratingPropValue == null || (ratingPropValue.count ?? 0) === 0) {
              return defaultSortValue
            }

            const totalRatings = ratingPropValue.count ?? 0
            const avgRating = (ratingPropValue.sum ?? 0) / totalRatings

            const bayesAverage = calculateBayesianAverage(totalRatings, avgRating, globalAverage, confidenceNumber)

            return bayesAverage
          },
          getPostPublishedAt,
        ],
        [sortOrder, 'asc'],
      )
    }
    default: {
      return sortPostsBySortIndex(posts)
    }
  }
}

function sortPostsAlphabeticallyByAttribute(
  posts: Post[],
  getAttribute: (post) => string | undefined,
  order: SortOrder,
): Post[] {
  const sortedPosts = [...posts]
  sortedPosts.sort((postA, postB) => {
    let postAAttribute = getAttribute(postA) ?? ''
    let postBAttribute = getAttribute(postB) ?? ''

    // Empty strings should always be sorted last
    const emptyStringCompValue = getEmptyStringComparisonValue(postAAttribute, postBAttribute)
    if (emptyStringCompValue !== 0) return emptyStringCompValue

    // For non-empty strings, sort to back if they are emoji
    const emojiCompValue = getEmojiOnlyStringComparisonValue(postAAttribute, postBAttribute)
    if (emojiCompValue !== 0) return emojiCompValue

    postAAttribute = stripEmojis(postAAttribute)
    postBAttribute = stripEmojis(postBAttribute)

    let comparison = 0
    if (order === 'asc') {
      //  https:developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator/Collator#options
      comparison = postAAttribute.localeCompare(postBAttribute, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    } else {
      comparison = postBAttribute.localeCompare(postAAttribute, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    }

    if (comparison === 0) {
      //  Secondary sort: publish date asc
      return new Date(getPostPublishedAt(postA) ?? 0).getTime() - new Date(getPostPublishedAt(postB) ?? 0).getTime()
    }

    return comparison
  })

  return sortedPosts
}

function sortPostsBySortIndex(posts: Post[]): Post[] {
  return sortBy(posts, [
    (post: Post): number | undefined => {
      if (post.sort_index != null) {
        return post.sort_index
      }
      const currentRank = parseInt(post.rank ?? '') + new Date(post.created_at as string).getTime()
      const oldRank = parseInt(currentRank.toString(), 10)
      return Math.abs(getAbsoluteSortIndex(oldRank))
    },
  ]).reverse()
}

/**
 * Calculate Bayesian average to balance rating quality with quantity.
 *
 * Problem: Simple averages can be misleading. A post with 1 rating of 5 stars (avg: 5.0)
 * would rank higher than a post with 100 ratings averaging 4.5 stars (avg: 4.5), even though
 * the second post is clearly more reliable and popular.
 *
 * Solution: Bayesian average "pulls" posts with few ratings toward the global average,
 * while posts with many ratings stay close to their actual average. This prevents new posts
 * with one lucky rating from dominating the sort.
 *
 * Formula: (numRatings × avgRatings + globalAvg × confidence) / (numRatings + confidence)
 *
 * Example (global avg = 3.5, confidence = 10):
 * - Post with 1 rating @ 5.0:   (1×5.0 + 3.5×10) / (1+10)   = 3.64 ← adjusted down
 * - Post with 100 ratings @ 4.5: (100×4.5 + 3.5×10) / (100+10) = 4.41 ← stays near 4.5
 *
 * @param numRatings - Number of ratings this post has received
 * @param avgRatings - This post's average rating (sum / count)
 * @param arithmeticAverage - Global average rating across all posts
 * @param confidenceNumber - How many ratings needed to "trust" a post (typically median count)
 * @returns Confidence-adjusted rating for sorting
 *
 * Implementation based on https://www.algolia.com/doc/guides/managing-results/must-do/custom-ranking/how-to/bayesian-average/
 */
function calculateBayesianAverage(
  numRatings: number,
  avgRatings: number,
  arithmeticAverage: number,
  confidenceNumber: number,
): number {
  return (numRatings * avgRatings + arithmeticAverage * confidenceNumber) / (numRatings + confidenceNumber)
}

function sortPostsByReactionSum(
  posts: Post[],
  reactionsByWishId: AccumulatedReactions,
  constants?: { reactionsTotalSumAverage: number; confidenceNumber: number },
): Post[] {
  if (reactionsByWishId == null) return posts
  return orderBy(
    posts,
    [
      (post: Post): number | undefined => {
        const postId = post?.id

        if (postId == null) return 0

        const reaction = reactionsByWishId[postId]
        if (reaction == null) return 0

        // Differentiate between posts with no reactions vs reactions that sum to 0
        if (isNonEmojiReactionData(reaction) && reaction.sum === 0) {
          if (reaction.totalReactionsCount > 0) {
            return 0.5
          }
          return 0
        }

        const bayesianAverageReactionTypes: Array<Exclude<ReactionType, 'like' | 'vote'>> = [
          ReactionType.Star,
          ReactionType.Grade,
        ]
        if (bayesianAverageReactionTypes.includes(reaction?.reactionType as Exclude<ReactionType, 'like' | 'vote'>)) {
          // sorting is based on bayesian average
          const reactionsTotalSumAverage = constants?.reactionsTotalSumAverage ?? 0
          const confidenceNumber = constants?.confidenceNumber ?? 100 // backup value

          const totalReactions = reaction?.totalReactionsCount
          const averageReactionSum = reaction.reactionType !== 'emoji' ? reaction.sum / totalReactions : 0
          const bayesAverage = calculateBayesianAverage(
            totalReactions,
            averageReactionSum,
            reactionsTotalSumAverage,
            confidenceNumber,
          )

          return bayesAverage
        }

        // for likes and votes, sorting is based on number of likes which is captured by sum
        return reaction.reactionType !== 'emoji' ? reaction.sum : 0
      },
      getPostPublishedAt,
    ],
    ['asc', 'desc'],
  )
}

function sortPostsByCreationDate(posts: Post[]): Post[] {
  return sortBy(posts, [getPostPublishedAt])
}

function sortPostsBySubject(posts: Post[], order: SortOrder): Post[] {
  return sortPostsAlphabeticallyByAttribute(posts, (post: Post): string | undefined => post.subject, order)
}

function getEmptyStringComparisonValue(a: string, b: string): number {
  if ((a === '' && b === '') || (a !== '' && b !== '')) {
    return 0
  } else if (a === '') {
    return 1
  } else {
    // b === ''
    return -1
  }
}

function getEmojiOnlyStringComparisonValue(a: string, b: string): number {
  try {
    const PURE_EMOJI_STRING_REGEX = new RegExp('^\\p{Extended_Pictographic}+$', 'u')

    const isAEmojisOnly = PURE_EMOJI_STRING_REGEX.test(a)
    const isBEmojisOnly = PURE_EMOJI_STRING_REGEX.test(b)
    if (isAEmojisOnly && !isBEmojisOnly) {
      return 1 // a comes after b
    } else if (!isAEmojisOnly && isBEmojisOnly) {
      return -1 // a comes before b
    } else {
      return 0 // don't swap
    }
  } catch {
    return 0
  }
}

function stripEmojis(str: string): string {
  try {
    const MATCH_EMOJI_REGEX = new RegExp(
      '(?![*#0-9]+)[\\p{Emoji}\\p{Emoji_Modifier}\\p{Emoji_Component}\\p{Emoji_Modifier_Base}\\p{Emoji_Presentation}]\\s*',
      'gu',
    )
    return str.replace(MATCH_EMOJI_REGEX, '')
  } catch {
    return str
  }
}

function recomputeSortIndex(
  curSortIndex: number | undefined,
  postsSortedBySortIndex: Post[],
  isTimelineV1 = false,
): number | undefined {
  if (curSortIndex === undefined || postsSortedBySortIndex.length < 1) {
    return curSortIndex
  }

  // check if a post with sort_index === curSortIndex already exists. If so, compute new sort_index that goes after it
  const beforePostIndex = postsSortedBySortIndex.findIndex((p) => p.sort_index === curSortIndex)

  // no collision
  if (beforePostIndex < 0) {
    return curSortIndex
  }

  const beforePostId = postsSortedBySortIndex[beforePostIndex].id
  const afterPostId =
    beforePostIndex === postsSortedBySortIndex.length - 1 && postsSortedBySortIndex.length === 1
      ? null
      : postsSortedBySortIndex[beforePostIndex + 1].id

  if (afterPostId == null || beforePostId == null) return 0
  // TimelineV1 sort index is from smallest on the left to largest to the right
  // while it's from largest to smallest on other formats
  const { sortIndex } = computePostIndexFromPost(null, {
    prevId: isTimelineV1 ? `wish-${afterPostId}` : `wish-${beforePostId}`,
    nextId: isTimelineV1 ? `wish-${beforePostId}` : `wish-${afterPostId}`,
  })

  return sortIndex ?? 0
}

function sortPinnedPostsToFront(posts: Post[]): Post[] {
  return [...posts].sort((postA, postB) => {
    if (isPostPinned(postA) && !isPostPinned(postB)) return -1
    if (!isPostPinned(postA) && isPostPinned(postB)) return 1
    return 0
  })
}

export {
  computeAndSaveNewPostIndex,
  computePostIndexFromPost,
  getBottomSortIndex,
  getNewUnpinSortIndex,
  getSortIndex,
  getTopSortIndex,
  recomputeSortIndex,
  sortPinnedPostsToFront,
  sortPostsByCreationDate,
  sortPostsByCustomProp,
  sortPostsByReactionSum,
  sortPostsBySortIndex,
  sortPostsBySubject,
}
